import type {
  RegistryAuthMethod,
  RegistryPackageManager
} from "@/api/useIntegrations.types"

// Mirrors api-integrations: what each package manager's resolver can write today. The
// server enforces it too; the form uses it to offer only methods that will work.
export const ALLOWED_AUTH_METHODS: Record<
  RegistryPackageManager,
  RegistryAuthMethod[]
> = {
  npm: ["token"],
  // The Maven resolver writes x-access-token as the username; Gradle takes the token alone.
  maven: ["token"],
  gradle: ["token"],
  python: ["username_token"],
  rubygems: ["username_password"],
  sbt: ["username_password"]
}

export const PACKAGE_MANAGERS: RegistryPackageManager[] = [
  "npm",
  "maven",
  "gradle",
  "python",
  "rubygems",
  "sbt"
]

export const AUTH_METHODS: RegistryAuthMethod[] = [
  "token",
  "username_token",
  "username_password"
]

const PUBLIC_REGISTRY_HOSTS = [
  "registry.npmjs.org",
  "registry.yarnpkg.com",
  "repo1.maven.org",
  "repo.maven.apache.org",
  "pypi.org",
  "files.pythonhosted.org",
  "rubygems.org"
]

const NPM_SCOPE_REGEX = /^@[a-z0-9~-][a-z0-9._~-]*$/

export const getAllowedAuthMethods = (
  packageManagers: RegistryPackageManager[] = []
): RegistryAuthMethod[] => {
  if (!packageManagers.length) return []

  return AUTH_METHODS.filter((method) =>
    packageManagers.every((pm) => ALLOWED_AUTH_METHODS[pm]?.includes(method))
  )
}

// The first two ticked package managers that cannot share an entry, if any.
export const getIncompatiblePair = (
  packageManagers: RegistryPackageManager[] = []
): [RegistryPackageManager, RegistryPackageManager] | null => {
  for (let i = 0; i < packageManagers.length; i++) {
    for (let j = i + 1; j < packageManagers.length; j++) {
      const pair: [RegistryPackageManager, RegistryPackageManager] = [
        packageManagers[i],
        packageManagers[j]
      ]

      if (!getAllowedAuthMethods(pair).length) return pair
    }
  }

  return null
}

export const hasUsername = (authMethod?: RegistryAuthMethod) =>
  authMethod === "username_token" || authMethod === "username_password"

export const isPasswordMethod = (authMethod?: RegistryAuthMethod) =>
  authMethod === "username_password"

export type EndpointProblem = "invalid" | "https" | "public" | "credentials"

export const getEndpointProblem = (
  value?: string
): { problem: EndpointProblem; host?: string } | null => {
  const endpoint = value?.trim()

  if (!endpoint) return null

  let url: URL

  try {
    url = new URL(endpoint)
  } catch {
    return { problem: "invalid" }
  }

  if (url.protocol !== "https:") return { problem: "https" }
  if (url.username || url.password) return { problem: "credentials" }

  const host = url.hostname.toLowerCase()

  if (PUBLIC_REGISTRY_HOSTS.includes(host)) return { problem: "public", host }

  return null
}

export const normalizeScope = (scope: string) => {
  const trimmed = scope.trim().toLowerCase()

  if (!trimmed) return ""

  return trimmed.startsWith("@") ? trimmed : `@${trimmed}`
}

export const isValidScope = (scope: string) =>
  NPM_SCOPE_REGEX.test(normalizeScope(scope))
